/**
 * FeedbackForm — Bug/Feature refinement modal for extension pages.
 *
 * P39 F4 (WI#844): Users who click "Submit Bug/Feature Request" (from
 * ChatWidget or directly) open this modal. The form submits to POST /api/feedback
 * and shows a confirmation with the tracking ID.
 */
import React, { useState } from 'react';
import { colors, spacing, typography, radius } from '../theme';
const PUBLISHER = 'cool-tools';
export const FeedbackForm = ({ extensionId, extensionName, onClose, initialType = 'bug', }) => {
    const [form, setForm] = useState({
        type: initialType,
        title: '',
        description: '',
        email: '',
    });
    const [submit, setSubmit] = useState({ status: 'idle' });
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.description.trim())
            return;
        setSubmit({ status: 'submitting' });
        try {
            const res = await fetch('/api/feedback', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    extensionId: `${PUBLISHER}.${extensionId}`,
                    type: form.type,
                    title: form.title.trim(),
                    description: form.description.trim(),
                    email: form.email.trim() || undefined,
                }),
            });
            if (res.ok) {
                const data = (await res.json());
                setSubmit({ status: 'success', trackingId: data.trackingId });
            }
            else if (res.status === 429) {
                setSubmit({ status: 'error', errorMessage: 'Too many requests. Please try again later.' });
            }
            else {
                const err = (await res.json().catch(() => ({ error: 'Unknown error' })));
                setSubmit({ status: 'error', errorMessage: err.error ?? 'Submission failed. Please try again.' });
            }
        }
        catch {
            setSubmit({ status: 'error', errorMessage: 'Network error. Please try again or email support@cool-tools.app.' });
        }
    };
    const labelStyle = {
        fontFamily: typography.fontFamily,
        fontSize: typography.fontSizeSm,
        fontWeight: 600,
        color: colors.textPrimary,
        display: 'block',
        marginBottom: 4,
    };
    const inputStyle = {
        width: '100%',
        padding: `${spacing.xs}px ${spacing.sm}px`,
        border: `1px solid ${colors.border}`,
        borderRadius: radius.card,
        fontFamily: typography.fontFamily,
        fontSize: typography.fontSizeSm,
        boxSizing: 'border-box',
        outline: 'none',
    };
    return (
    /* Backdrop */
    React.createElement("div", { style: {
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: spacing.md,
        }, onClick: (e) => { if (e.target === e.currentTarget)
            onClose(); } },
        React.createElement("div", { style: {
                background: colors.white,
                borderRadius: radius.card,
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                width: '100%',
                maxWidth: 520,
                maxHeight: '90vh',
                overflowY: 'auto',
            } },
            React.createElement("div", { style: {
                    padding: `${spacing.md}px ${spacing.lg}px`,
                    borderBottom: `1px solid ${colors.border}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                } },
                React.createElement("div", null,
                    React.createElement("h2", { style: {
                            fontFamily: typography.fontFamily,
                            fontSize: typography.fontSizeLg,
                            fontWeight: 700,
                            color: colors.textPrimary,
                            margin: 0,
                        } }, submit.status === 'success' ? 'Thank You!' : 'Report Bug / Feature Request'),
                    React.createElement("p", { style: {
                            fontFamily: typography.fontFamily,
                            fontSize: typography.fontSizeSm,
                            color: colors.textSecondary,
                            margin: `${spacing.xs}px 0 0`,
                        } }, extensionName)),
                React.createElement("button", { onClick: onClose, style: {
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: colors.textSecondary,
                        fontSize: 20,
                        lineHeight: 1,
                        padding: 4,
                    }, "aria-label": "Close" }, "\u00D7")),
            React.createElement("div", { style: { padding: `${spacing.lg}px` } }, submit.status === 'success' ? (
            /* Success state */
            React.createElement("div", { style: { textAlign: 'center', padding: `${spacing.xl}px 0` } },
                React.createElement("div", { style: { fontSize: 48, marginBottom: spacing.md } }, "\u2705"),
                React.createElement("h3", { style: {
                        fontFamily: typography.fontFamily,
                        fontSize: typography.fontSizeLg,
                        fontWeight: 700,
                        color: colors.textPrimary,
                        margin: `0 0 ${spacing.sm}px`,
                    } }, "Feedback Received"),
                React.createElement("p", { style: {
                        fontFamily: typography.fontFamily,
                        fontSize: typography.fontSizeSm,
                        color: colors.textSecondary,
                        margin: `0 0 ${spacing.md}px`,
                    } }, "Your tracking ID is:"),
                React.createElement("div", { style: {
                        background: colors.surface,
                        border: `1px solid ${colors.border}`,
                        borderRadius: radius.card,
                        padding: `${spacing.sm}px ${spacing.md}px`,
                        fontFamily: 'monospace',
                        fontSize: typography.fontSizeSm,
                        color: colors.primary,
                        letterSpacing: '0.05em',
                        marginBottom: spacing.lg,
                        wordBreak: 'break-all',
                    } }, submit.trackingId),
                React.createElement("p", { style: {
                        fontFamily: typography.fontFamily,
                        fontSize: typography.fontSizeXs,
                        color: colors.textMuted,
                        margin: `0 0 ${spacing.lg}px`,
                    } },
                    "We'll review your report and reach out if we need more details.",
                    form.email && ' You\'ll receive an email update.'),
                React.createElement("button", { onClick: onClose, style: {
                        padding: `${spacing.sm}px ${spacing.xl}px`,
                        background: colors.primary,
                        color: colors.white,
                        border: 'none',
                        borderRadius: radius.card,
                        cursor: 'pointer',
                        fontFamily: typography.fontFamily,
                        fontSize: typography.fontSizeSm,
                        fontWeight: 600,
                    } }, "Close"))) : (
            /* Form */
            React.createElement("form", { onSubmit: (e) => void handleSubmit(e) },
                React.createElement("div", { style: { marginBottom: spacing.md } },
                    React.createElement("label", { style: labelStyle }, "Type"),
                    React.createElement("div", { style: { display: 'flex', gap: spacing.sm } }, ['bug', 'feature'].map((t) => (React.createElement("button", { key: t, type: "button", onClick: () => setForm((f) => ({ ...f, type: t })), style: {
                            flex: 1,
                            padding: `${spacing.xs}px ${spacing.sm}px`,
                            border: `2px solid ${form.type === t ? colors.primary : colors.border}`,
                            borderRadius: radius.card,
                            cursor: 'pointer',
                            background: form.type === t ? colors.primary + '12' : colors.white,
                            color: form.type === t ? colors.primary : colors.textSecondary,
                            fontFamily: typography.fontFamily,
                            fontSize: typography.fontSizeSm,
                            fontWeight: form.type === t ? 700 : 500,
                            transition: 'all 0.15s ease',
                        } }, t === 'bug' ? '🐛 Bug Report' : '✨ Feature Request'))))),
                React.createElement("div", { style: { marginBottom: spacing.md } },
                    React.createElement("label", { style: labelStyle, htmlFor: "fb-title" },
                        "Title ",
                        React.createElement("span", { style: { color: colors.error } }, "*")),
                    React.createElement("input", { id: "fb-title", type: "text", value: form.title, onChange: (e) => setForm((f) => ({ ...f, title: e.target.value })), placeholder: form.type === 'bug' ? 'Short description of the bug…' : 'What feature would you like?', maxLength: 140, required: true, style: inputStyle }),
                    React.createElement("div", { style: {
                            fontFamily: typography.fontFamily,
                            fontSize: typography.fontSizeXs,
                            color: form.title.length > 120 ? colors.error : colors.textMuted,
                            textAlign: 'right',
                            marginTop: 2,
                        } },
                        form.title.length,
                        "/140")),
                React.createElement("div", { style: { marginBottom: spacing.md } },
                    React.createElement("label", { style: labelStyle, htmlFor: "fb-description" },
                        "Description ",
                        React.createElement("span", { style: { color: colors.error } }, "*")),
                    React.createElement("textarea", { id: "fb-description", value: form.description, onChange: (e) => setForm((f) => ({ ...f, description: e.target.value })), placeholder: form.type === 'bug'
                            ? 'Steps to reproduce:\n1. …\n2. …\nExpected: …\nActual: …'
                            : 'Describe the feature you\'d like and how it would help you…', rows: 5, maxLength: 4000, required: true, style: { ...inputStyle, resize: 'vertical', minHeight: 100 } }),
                    React.createElement("div", { style: {
                            fontFamily: typography.fontFamily,
                            fontSize: typography.fontSizeXs,
                            color: form.description.length > 3800 ? colors.error : colors.textMuted,
                            textAlign: 'right',
                            marginTop: 2,
                        } },
                        form.description.length,
                        "/4000")),
                React.createElement("div", { style: { marginBottom: spacing.lg } },
                    React.createElement("label", { style: labelStyle, htmlFor: "fb-email" },
                        "Email ",
                        React.createElement("span", { style: { color: colors.textMuted, fontWeight: 400 } }, "(optional \u2014 for updates)")),
                    React.createElement("input", { id: "fb-email", type: "email", value: form.email, onChange: (e) => setForm((f) => ({ ...f, email: e.target.value })), placeholder: "you@example.com", style: inputStyle })),
                submit.status === 'error' && (React.createElement("div", { style: {
                        padding: `${spacing.sm}px ${spacing.md}px`,
                        background: colors.errorBg,
                        border: `1px solid ${colors.error}40`,
                        borderRadius: radius.card,
                        fontFamily: typography.fontFamily,
                        fontSize: typography.fontSizeSm,
                        color: colors.error,
                        marginBottom: spacing.md,
                    } }, submit.errorMessage)),
                React.createElement("div", { style: { display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' } },
                    React.createElement("button", { type: "button", onClick: onClose, style: {
                            padding: `${spacing.sm}px ${spacing.lg}px`,
                            background: 'none',
                            border: `1px solid ${colors.border}`,
                            borderRadius: radius.card,
                            cursor: 'pointer',
                            fontFamily: typography.fontFamily,
                            fontSize: typography.fontSizeSm,
                            color: colors.textSecondary,
                        } }, "Cancel"),
                    React.createElement("button", { type: "submit", disabled: submit.status === 'submitting' || !form.title.trim() || !form.description.trim(), style: {
                            padding: `${spacing.sm}px ${spacing.lg}px`,
                            background: colors.primary,
                            color: colors.white,
                            border: 'none',
                            borderRadius: radius.card,
                            cursor: submit.status === 'submitting' ? 'not-allowed' : 'pointer',
                            opacity: submit.status === 'submitting' ? 0.7 : 1,
                            fontFamily: typography.fontFamily,
                            fontSize: typography.fontSizeSm,
                            fontWeight: 600,
                        } }, submit.status === 'submitting' ? 'Submitting…' : 'Submit'))))))));
};
